import React, { RefObject } from 'react';
import styled from 'styled-components';

import { body2, colors, heading4, spacing } from 'ui/theme';
import { HelpTooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import noop from 'utils/noop';

const OptionWrapper = styled.div<{ $isSelected: boolean }>`
  display: inline-flex;
  padding: ${spacing.plusHalf};
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;

  align-content: space-around;
  border-radius: ${spacing.half};
  background: ${colors.grey1};
  border: ${pxToRem(3)} solid ${colors.white};

  :focus {
    outline: none;
    ${({ $isSelected }) =>
      !$isSelected &&
      `
      border: ${pxToRem(1.5)} solid ${colors.grey50};
      margin: ${pxToRem(1.5)};
  `}
  }

  ${({ $isSelected }) =>
    $isSelected &&
    `
    border: ${pxToRem(3)} solid ${colors.purple50};
  `}
`;

const OptionText = styled.span`
  ${heading4}
  margin-right: ${spacing.half};
`;

const BlockWrapper = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const LevelWrapper = styled(BlockWrapper)`
  margin-top: ${spacing.plusHalf};
  background: ${colors.white};
  border-radius: ${pxToRem(6)};
  padding: ${spacing.threeQuarter};
`;

const LevelText = styled.span`
  ${body2}
  margin-left: ${spacing.half};
`;

const PNGWrapper = styled.img`
  height: ${pxToRem(137)};
  width: ${pxToRem(214)};
`;

type TemplateEditorOptionProps = {
  /* content strings to render */
  content: Record<string, any>;
  /* Function to select an editor */
  setSelectedEditor: (arg: string) => void;
  /* String of the option selected */
  selectedEditor: string;
};

export const TemplateEditorOption = React.forwardRef(
  (
    { setSelectedEditor = noop, selectedEditor, content, ...props }: TemplateEditorOptionProps,
    ref: React.ForwardedRef<HTMLElement>,
  ) => (
    <OptionWrapper
      data-test={content.title}
      tabIndex={0}
      onClick={() => setSelectedEditor(content.title)}
      $isSelected={content.title === selectedEditor}
      ref={ref as RefObject<HTMLDivElement>}
      {...props}
    >
      <BlockWrapper>
        <PNGWrapper src={content.image} alt={`${content.title}-image`} />
      </BlockWrapper>
      <BlockWrapper>
        <OptionText>{content.title}</OptionText>
        <HelpTooltip tooltipContent={content.tooltip} />
      </BlockWrapper>
      <LevelWrapper>
        <content.level />
        <LevelText>{content.levelText}</LevelText>
      </LevelWrapper>
    </OptionWrapper>
  ),
);
